import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiPrinter, FiDownload, FiCalendar, FiSearch, FiX, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import reportesContablesService from '../services/reportesContablesService';
import Loading from '../components/Loading';
import './BalanceGeneralPage.css';

const BalanceGeneralPage = () => {
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [exportando, setExportando] = useState(false);
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [analisisData, setAnalisisData] = useState(null);
  const [loadingAnalisis, setLoadingAnalisis] = useState(false);
  const [expandedMovimientos, setExpandedMovimientos] = useState(false);
  const [movimientoSeleccionado, setMovimientoSeleccionado] = useState(null);
  const [mostrarModalVoucher, setMostrarModalVoucher] = useState(false);
  const [voucherData, setVoucherData] = useState(null);
  const [loadingVoucher, setLoadingVoucher] = useState(false);

  useEffect(() => {
    cargarBalance();
  }, []);

  const cargarBalance = async (fechaConsulta = fecha) => {
    try {
      setLoading(true);
      const response = await reportesContablesService.getBalanceGeneral(fechaConsulta);
      setBalanceData(response.data);
    } catch (error) {
      console.error('Error al cargar balance general:', error);
      toast.error(error.response?.data?.error || 'Error al cargar el balance general');
    } finally {
      setLoading(false);
    }
  };

  const handleFechaChange = (e) => {
    const nuevaFecha = e.target.value;
    setFecha(nuevaFecha);
    cargarBalance(nuevaFecha);
  };

  const handleImprimir = () => {
    window.print();
  };

  const handleExportarPDF = async () => {
    try {
      setExportando(true);
      await reportesContablesService.exportBalanceToPDF(
        balanceData,
        fecha,
        balanceData.cuenta.nombre
      );
      toast.success('Balance exportado a PDF correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      toast.error('Error al exportar a PDF');
    } finally {
      setExportando(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getIndentClass = (nivel) => {
    return `indent-${Math.min(nivel, 4)}`;
  };

  const handleSeleccionarCuenta = (cuenta) => {
    if (cuentaSeleccionada?.id === cuenta.id) {
      setCuentaSeleccionada(null);
    } else {
      setCuentaSeleccionada(cuenta);
    }
  };

  const handleAnalizarCuenta = async () => {
    if (cuentaSeleccionada) {
      setMostrarModal(true);
      setLoadingAnalisis(true);
      setExpandedMovimientos(false);
      try {
        // Cargar análisis para la cuenta seleccionada
        const haceUnMes = new Date();
        haceUnMes.setMonth(haceUnMes.getMonth() - 1);
        const fechaDesde = haceUnMes.toISOString().split('T')[0];

        const response = await reportesContablesService.getAnalisisCuentas(
          fechaDesde,
          fecha,
          null, // tipo_cuenta
          true  // solo con movimientos
        );

        // Filtrar solo la cuenta seleccionada
        const cuentaAnalisis = response.data.cuentas.find(c => c.id === cuentaSeleccionada.id);
        if (cuentaAnalisis) {
          setAnalisisData(cuentaAnalisis);
        } else {
          toast.error('No se encontró información para esta cuenta');
        }
      } catch (error) {
        console.error('Error al cargar análisis:', error);
        toast.error('Error al cargar el análisis de la cuenta');
      } finally {
        setLoadingAnalisis(false);
      }
    }
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setAnalisisData(null);
    setExpandedMovimientos(false);
  };

  const handleSeleccionarMovimiento = async (movimiento) => {
    setMovimientoSeleccionado(movimiento);
    setMostrarModalVoucher(true);
    setLoadingVoucher(true);
    try {
      // Obtener el asiento contable completo
      const response = await reportesContablesService.getAsientoContable(movimiento.asiento_id);
      setVoucherData(response.data);
    } catch (error) {
      console.error('Error al cargar voucher:', error);
      toast.error('Error al cargar el voucher');
    } finally {
      setLoadingVoucher(false);
    }
  };

  const cerrarModalVoucher = () => {
    setMostrarModalVoucher(false);
    setVoucherData(null);
    setMovimientoSeleccionado(null);
  };

  if (loading) {
    return <Loading />;
  }

  if (!balanceData) {
    return (
      <div className="balance-general-page">
        <div className="error-message">No se pudo cargar el balance general</div>
      </div>
    );
  }

  const balanceado = balanceData.ecuacion_contable.diferencia === 0;

  return (
    <div className="balance-general-page">
      {/* Header */}
      <div className="balance-header no-print">
        <div className="balance-title-section">
          <h1>Balance General</h1>
          <p className="balance-subtitle">{balanceData.cuenta.nombre}</p>
        </div>

        <div className="balance-controls">
          <button
            onClick={handleAnalizarCuenta}
            className="btn-analizar"
            disabled={!cuentaSeleccionada}
            title="Analizar Cuenta Seleccionada"
          >
            <FiSearch />
            <span>Analizar Cuenta</span>
          </button>

          <div className="fecha-control">
            <FiCalendar className="icon" />
            <input
              type="date"
              value={fecha}
              onChange={handleFechaChange}
              className="fecha-input"
            />
          </div>

          <button onClick={() => cargarBalance()} className="btn-refresh" title="Actualizar">
            <FiRefreshCw />
          </button>

          <button onClick={handleImprimir} className="btn-print" title="Imprimir">
            <FiPrinter />
          </button>

          <button
            onClick={handleExportarPDF}
            className="btn-download"
            disabled={exportando}
            title="Exportar a PDF"
          >
            <FiDownload />
            {exportando && <span className="spinner-small"></span>}
          </button>
        </div>
      </div>

      {/* Report Content */}
      <div className="balance-content">
        {/* BALANCE GENERAL - Tabla Unificada */}
        <div className="balance-section">
          <div className="table-responsive">
            <table className="balance-table balance-table-detailed">
              <thead>
                <tr>
                  <th className="col-codigo">Cuenta</th>
                  <th className="col-nombre">Nombre</th>
                  <th className="col-numero">Debe</th>
                  <th className="col-numero">Haber</th>
                  <th className="col-numero">Deudor</th>
                  <th className="col-numero">Acreedor</th>
                  <th className="col-numero">Activo</th>
                  <th className="col-numero">Pasivo</th>
                  <th className="col-numero">Pérdida</th>
                  <th className="col-numero">Ganancia</th>
                </tr>
              </thead>
              <tbody>
                {/* ACTIVOS */}
                {balanceData.activos.length > 0 && (
                  <>
                    <tr className="section-separator">
                      <td colSpan="10" className="section-label activos-label">ACTIVOS</td>
                    </tr>
                    {balanceData.activos.map((cuenta) => (
                      <tr
                        key={`activo-${cuenta.id}`}
                        onClick={() => handleSeleccionarCuenta(cuenta)}
                        className={cuentaSeleccionada?.id === cuenta.id ? 'selected-row' : 'selectable-row'}
                      >
                        <td className="account-code">{cuenta.codigo}</td>
                        <td className={`account-name ${getIndentClass(cuenta.nivel)}`}>
                          {cuenta.nombre}
                        </td>
                        <td className="account-number">{formatCurrency(cuenta.debe)}</td>
                        <td className="account-number">{formatCurrency(cuenta.haber)}</td>
                        <td className="account-number">{formatCurrency(cuenta.deudor)}</td>
                        <td className="account-number">{formatCurrency(cuenta.acreedor)}</td>
                        <td className="account-number">{formatCurrency(cuenta.activo)}</td>
                        <td className="account-number">{formatCurrency(cuenta.pasivo)}</td>
                        <td className="account-number">{formatCurrency(cuenta.perdida)}</td>
                        <td className="account-number">{formatCurrency(cuenta.ganancia)}</td>
                      </tr>
                    ))}
                  </>
                )}

                {/* PASIVOS */}
                {balanceData.pasivos.length > 0 && (
                  <>
                    <tr className="section-separator">
                      <td colSpan="10" className="section-label pasivos-label">PASIVOS</td>
                    </tr>
                    {balanceData.pasivos.map((cuenta) => (
                      <tr
                        key={`pasivo-${cuenta.id}`}
                        onClick={() => handleSeleccionarCuenta(cuenta)}
                        className={cuentaSeleccionada?.id === cuenta.id ? 'selected-row' : 'selectable-row'}
                      >
                        <td className="account-code">{cuenta.codigo}</td>
                        <td className={`account-name ${getIndentClass(cuenta.nivel)}`}>
                          {cuenta.nombre}
                        </td>
                        <td className="account-number">{formatCurrency(cuenta.debe)}</td>
                        <td className="account-number">{formatCurrency(cuenta.haber)}</td>
                        <td className="account-number">{formatCurrency(cuenta.deudor)}</td>
                        <td className="account-number">{formatCurrency(cuenta.acreedor)}</td>
                        <td className="account-number">{formatCurrency(cuenta.activo)}</td>
                        <td className="account-number">{formatCurrency(cuenta.pasivo)}</td>
                        <td className="account-number">{formatCurrency(cuenta.perdida)}</td>
                        <td className="account-number">{formatCurrency(cuenta.ganancia)}</td>
                      </tr>
                    ))}
                  </>
                )}

                {/* PATRIMONIO */}
                {balanceData.patrimonio.length > 0 && (
                  <>
                    <tr className="section-separator">
                      <td colSpan="10" className="section-label patrimonio-label">PATRIMONIO</td>
                    </tr>
                    {balanceData.patrimonio.map((cuenta) => (
                      <tr
                        key={`patrimonio-${cuenta.id}`}
                        onClick={() => handleSeleccionarCuenta(cuenta)}
                        className={cuentaSeleccionada?.id === cuenta.id ? 'selected-row' : 'selectable-row'}
                      >
                        <td className="account-code">{cuenta.codigo}</td>
                        <td className={`account-name ${getIndentClass(cuenta.nivel)}`}>
                          {cuenta.nombre}
                        </td>
                        <td className="account-number">{formatCurrency(cuenta.debe)}</td>
                        <td className="account-number">{formatCurrency(cuenta.haber)}</td>
                        <td className="account-number">{formatCurrency(cuenta.deudor)}</td>
                        <td className="account-number">{formatCurrency(cuenta.acreedor)}</td>
                        <td className="account-number">{formatCurrency(cuenta.activo)}</td>
                        <td className="account-number">{formatCurrency(cuenta.pasivo)}</td>
                        <td className="account-number">{formatCurrency(cuenta.perdida)}</td>
                        <td className="account-number">{formatCurrency(cuenta.ganancia)}</td>
                      </tr>
                    ))}
                  </>
                )}

                {/* INGRESOS */}
                {balanceData.ingresos && balanceData.ingresos.length > 0 && (
                  <>
                    <tr className="section-separator">
                      <td colSpan="10" className="section-label ingresos-label">INGRESOS</td>
                    </tr>
                    {balanceData.ingresos.map((cuenta) => (
                      <tr
                        key={`ingreso-${cuenta.id}`}
                        onClick={() => handleSeleccionarCuenta(cuenta)}
                        className={cuentaSeleccionada?.id === cuenta.id ? 'selected-row' : 'selectable-row'}
                      >
                        <td className="account-code">{cuenta.codigo}</td>
                        <td className={`account-name ${getIndentClass(cuenta.nivel)}`}>
                          {cuenta.nombre}
                        </td>
                        <td className="account-number">{formatCurrency(cuenta.debe)}</td>
                        <td className="account-number">{formatCurrency(cuenta.haber)}</td>
                        <td className="account-number">{formatCurrency(cuenta.deudor)}</td>
                        <td className="account-number">{formatCurrency(cuenta.acreedor)}</td>
                        <td className="account-number">{formatCurrency(cuenta.activo)}</td>
                        <td className="account-number">{formatCurrency(cuenta.pasivo)}</td>
                        <td className="account-number">{formatCurrency(cuenta.perdida)}</td>
                        <td className="account-number">{formatCurrency(cuenta.ganancia)}</td>
                      </tr>
                    ))}
                  </>
                )}

                {/* GASTOS */}
                {balanceData.gastos && balanceData.gastos.length > 0 && (
                  <>
                    <tr className="section-separator">
                      <td colSpan="10" className="section-label gastos-label">GASTOS</td>
                    </tr>
                    {balanceData.gastos.map((cuenta) => (
                      <tr
                        key={`gasto-${cuenta.id}`}
                        onClick={() => handleSeleccionarCuenta(cuenta)}
                        className={cuentaSeleccionada?.id === cuenta.id ? 'selected-row' : 'selectable-row'}
                      >
                        <td className="account-code">{cuenta.codigo}</td>
                        <td className={`account-name ${getIndentClass(cuenta.nivel)}`}>
                          {cuenta.nombre}
                        </td>
                        <td className="account-number">{formatCurrency(cuenta.debe)}</td>
                        <td className="account-number">{formatCurrency(cuenta.haber)}</td>
                        <td className="account-number">{formatCurrency(cuenta.deudor)}</td>
                        <td className="account-number">{formatCurrency(cuenta.acreedor)}</td>
                        <td className="account-number">{formatCurrency(cuenta.activo)}</td>
                        <td className="account-number">{formatCurrency(cuenta.pasivo)}</td>
                        <td className="account-number">{formatCurrency(cuenta.perdida)}</td>
                        <td className="account-number">{formatCurrency(cuenta.ganancia)}</td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Sin datos */}
                {balanceData.activos.length === 0 &&
                 balanceData.pasivos.length === 0 &&
                 balanceData.patrimonio.length === 0 &&
                 (!balanceData.ingresos || balanceData.ingresos.length === 0) &&
                 (!balanceData.gastos || balanceData.gastos.length === 0) && (
                  <tr>
                    <td colSpan="10" className="no-data">No hay movimientos registrados</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td colSpan="2" className="totals-label">TOTALES</td>
                  <td className="totals-number">
                    {formatCurrency(
                      [...balanceData.activos, ...balanceData.pasivos, ...balanceData.patrimonio,
                       ...(balanceData.ingresos || []), ...(balanceData.gastos || [])]
                        .reduce((sum, cuenta) => sum + cuenta.debe, 0)
                    )}
                  </td>
                  <td className="totals-number">
                    {formatCurrency(
                      [...balanceData.activos, ...balanceData.pasivos, ...balanceData.patrimonio,
                       ...(balanceData.ingresos || []), ...(balanceData.gastos || [])]
                        .reduce((sum, cuenta) => sum + cuenta.haber, 0)
                    )}
                  </td>
                  <td className="totals-number">
                    {formatCurrency(
                      [...balanceData.activos, ...balanceData.pasivos, ...balanceData.patrimonio,
                       ...(balanceData.ingresos || []), ...(balanceData.gastos || [])]
                        .reduce((sum, cuenta) => sum + cuenta.deudor, 0)
                    )}
                  </td>
                  <td className="totals-number">
                    {formatCurrency(
                      [...balanceData.activos, ...balanceData.pasivos, ...balanceData.patrimonio,
                       ...(balanceData.ingresos || []), ...(balanceData.gastos || [])]
                        .reduce((sum, cuenta) => sum + cuenta.acreedor, 0)
                    )}
                  </td>
                  <td className="totals-number">
                    {formatCurrency(
                      [...balanceData.activos, ...balanceData.pasivos, ...balanceData.patrimonio,
                       ...(balanceData.ingresos || []), ...(balanceData.gastos || [])]
                        .reduce((sum, cuenta) => sum + cuenta.activo, 0)
                    )}
                  </td>
                  <td className="totals-number">
                    {formatCurrency(
                      [...balanceData.activos, ...balanceData.pasivos, ...balanceData.patrimonio,
                       ...(balanceData.ingresos || []), ...(balanceData.gastos || [])]
                        .reduce((sum, cuenta) => sum + cuenta.pasivo, 0)
                    )}
                  </td>
                  <td className="totals-number">
                    {formatCurrency(
                      [...balanceData.activos, ...balanceData.pasivos, ...balanceData.patrimonio,
                       ...(balanceData.ingresos || []), ...(balanceData.gastos || [])]
                        .reduce((sum, cuenta) => sum + cuenta.perdida, 0)
                    )}
                  </td>
                  <td className="totals-number">
                    {formatCurrency(
                      [...balanceData.activos, ...balanceData.pasivos, ...balanceData.patrimonio,
                       ...(balanceData.ingresos || []), ...(balanceData.gastos || [])]
                        .reduce((sum, cuenta) => sum + cuenta.ganancia, 0)
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Modal de Análisis de Cuenta */}
      {mostrarModal && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Análisis de Cuenta</h2>
              <button onClick={cerrarModal} className="btn-close-modal">
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              {loadingAnalisis ? (
                <div className="modal-loading">
                  <Loading fullScreen={false} mensaje="Cargando análisis" />
                </div>
              ) : analisisData ? (
                <>
                  {/* Información de la cuenta */}
                  <div className="cuenta-info">
                    <div className="info-row">
                      <span className="info-label">Código:</span>
                      <span className="info-value">{analisisData.codigo}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Nombre:</span>
                      <span className="info-value">{analisisData.nombre}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Tipo:</span>
                      <span className={`tipo-badge ${analisisData.tipo_cuenta.toLowerCase()}`}>
                        {analisisData.tipo_cuenta}
                      </span>
                    </div>
                  </div>

                  {/* Resumen de movimientos */}
                  <div className="resumen-movimientos">
                    <div className="resumen-item">
                      <div className="resumen-label">Total Debe</div>
                      <div className="resumen-valor debe">{formatCurrency(analisisData.total_debe)}</div>
                    </div>
                    <div className="resumen-item">
                      <div className="resumen-label">Total Haber</div>
                      <div className="resumen-valor haber">{formatCurrency(analisisData.total_haber)}</div>
                    </div>
                    <div className="resumen-item">
                      <div className="resumen-label">Saldo</div>
                      <div className={`resumen-valor ${analisisData.saldo === 0 ? 'balanceado' : 'saldo'}`}>
                        {formatCurrency(analisisData.saldo)}
                      </div>
                    </div>
                    <div className="resumen-item">
                      <div className="resumen-label">Movimientos</div>
                      <div className="resumen-valor">{analisisData.num_movimientos}</div>
                    </div>
                  </div>

                  {/* Detalle de movimientos */}
                  {analisisData.movimientos && analisisData.movimientos.length > 0 && (
                    <div className="movimientos-section">
                      <button
                        onClick={() => setExpandedMovimientos(!expandedMovimientos)}
                        className="btn-toggle-movimientos"
                      >
                        {expandedMovimientos ? <FiChevronDown /> : <FiChevronRight />}
                        <span>Ver Movimientos ({analisisData.num_movimientos})</span>
                      </button>

                      {expandedMovimientos && (
                        <div className="movimientos-tabla-container">
                          <table className="movimientos-tabla">
                            <thead>
                              <tr>
                                <th>Asiento</th>
                                <th>Fecha</th>
                                <th>Glosa</th>
                                <th>Tipo</th>
                                <th>Monto</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analisisData.movimientos.map((mov, idx) => (
                                <tr
                                  key={idx}
                                  onClick={() => handleSeleccionarMovimiento(mov)}
                                  className={movimientoSeleccionado?.asiento_id === mov.asiento_id && movimientoSeleccionado?.fecha === mov.fecha ? 'selected-mov-row' : 'selectable-mov-row'}
                                >
                                  <td>{mov.asiento_numero}</td>
                                  <td>{new Date(mov.fecha + 'T00:00:00').toLocaleDateString('es-CL')}</td>
                                  <td className="glosa-cell">{mov.glosa}</td>
                                  <td>
                                    <span className={`mov-tipo-badge ${mov.tipo_movimiento}`}>
                                      {mov.tipo_movimiento === 'debe' ? 'DEBE' : 'HABER'}
                                    </span>
                                  </td>
                                  <td className={`monto-cell ${mov.tipo_movimiento}`}>
                                    {formatCurrency(mov.monto)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="totales-movimientos-row">
                                <td colSpan="3" className="totales-label-modal">TOTALES</td>
                                <td className="totales-column-label">Total Debe:</td>
                                <td className="totales-valor debe">{formatCurrency(analisisData.total_debe)}</td>
                              </tr>
                              <tr className="totales-movimientos-row">
                                <td colSpan="3"></td>
                                <td className="totales-column-label">Total Haber:</td>
                                <td className="totales-valor haber">{formatCurrency(analisisData.total_haber)}</td>
                              </tr>
                              <tr className="totales-movimientos-row saldo-row">
                                <td colSpan="3"></td>
                                <td className="totales-column-label">Saldo:</td>
                                <td className={`totales-valor ${analisisData.saldo === 0 ? 'balanceado' : 'saldo'}`}>
                                  {formatCurrency(analisisData.saldo)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="modal-error">No se pudo cargar la información de la cuenta</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Voucher */}
      {mostrarModalVoucher && (
        <div className="modal-overlay" onClick={cerrarModalVoucher}>
          <div className="modal-content modal-voucher" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Voucher Contable</h2>
              <button onClick={cerrarModalVoucher} className="btn-close-modal">
                <FiX />
              </button>
            </div>

            <div className="modal-body">
              {loadingVoucher ? (
                <div className="modal-loading">
                  <Loading fullScreen={false} mensaje="Cargando voucher" />
                </div>
              ) : voucherData ? (
                <>
                  {/* Información del Asiento */}
                  <div className="voucher-info">
                    <div className="voucher-header-info">
                      <div className="info-group">
                        <span className="info-label">Asiento N°:</span>
                        <span className="info-value-large">{voucherData.numero}</span>
                      </div>
                      <div className="info-group">
                        <span className="info-label">Fecha:</span>
                        <span className="info-value">{new Date(voucherData.fecha + 'T00:00:00').toLocaleDateString('es-CL')}</span>
                      </div>
                      <div className="info-group">
                        <span className="info-label">Estado:</span>
                        <span className={`estado-badge ${voucherData.estado}`}>
                          {voucherData.estado.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="glosa-section">
                      <span className="info-label">Glosa:</span>
                      <p className="glosa-text">{voucherData.glosa}</p>
                    </div>
                  </div>

                  {/* Tabla de Detalles del Asiento */}
                  <div className="voucher-detalles-section">
                    <h3>Detalles del Asiento</h3>
                    <table className="voucher-table">
                      <thead>
                        <tr>
                          <th>Cuenta</th>
                          <th>Nombre</th>
                          <th>Debe</th>
                          <th>Haber</th>
                        </tr>
                      </thead>
                      <tbody>
                        {voucherData.detalles && voucherData.detalles.map((detalle, idx) => (
                          <tr key={idx}>
                            <td className="cuenta-codigo">{detalle.plan_cuenta_codigo}</td>
                            <td className="cuenta-nombre">{detalle.plan_cuenta_nombre}</td>
                            <td className={`monto-cell ${detalle.tipo_movimiento === 'debe' ? 'debe' : ''}`}>
                              {detalle.tipo_movimiento === 'debe' ? formatCurrency(detalle.monto) : '-'}
                            </td>
                            <td className={`monto-cell ${detalle.tipo_movimiento === 'haber' ? 'haber' : ''}`}>
                              {detalle.tipo_movimiento === 'haber' ? formatCurrency(detalle.monto) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="voucher-totales-row">
                          <td colSpan="2" className="totales-label">TOTALES</td>
                          <td className="totales-valor debe">
                            {formatCurrency(
                              voucherData.detalles?.filter(d => d.tipo_movimiento === 'debe')
                                .reduce((sum, d) => sum + parseFloat(d.monto), 0) || 0
                            )}
                          </td>
                          <td className="totales-valor haber">
                            {formatCurrency(
                              voucherData.detalles?.filter(d => d.tipo_movimiento === 'haber')
                                .reduce((sum, d) => sum + parseFloat(d.monto), 0) || 0
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Información adicional */}
                  {voucherData.documento_legal && (
                    <div className="voucher-documento">
                      <h4>Documento Relacionado</h4>
                      <div className="documento-info">
                        <span className="info-label">Tipo:</span>
                        <span className="info-value">{voucherData.documento_legal.tipo_documento}</span>
                        <span className="info-label">Número:</span>
                        <span className="info-value">{voucherData.documento_legal.numero_documento}</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="modal-error">No se pudo cargar el voucher</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BalanceGeneralPage;
